import { useState, useEffect, useCallback, useRef } from "react";
import App from "./App";
import BuddyFinderMode, { apiFetch } from "./modes/BuddyFinderMode";
import SocialFeedMode from "./modes/SocialFeedMode";
import { supabase } from "./supabase";

const UNREAD_POLL_MS = 10000;

const MODES = [
  { id: "coach", label: "Ski Coach" },
  { id: "buddyFinder", label: "Buddy Finder" },
  { id: "socialFeed", label: "Social Feed" },
];

const styles = {
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    margin: "8px 12px",
    padding: 4,
    borderRadius: 14,
    backgroundColor: "#0d1b2a",
    border: "1px solid #1e3a5f",
    fontFamily: "Georgia, 'Times New Roman', serif",
    boxSizing: "border-box",
  },
  tab: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 999,
    color: "#ddddee",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 14,
    cursor: "pointer",
    padding: "8px 10px",
    transition: "background-color 0.15s ease, color 0.15s ease",
  },
  tabActive: {
    backgroundColor: "#c9a84c",
    color: "#0d1b2a",
    fontWeight: 700,
  },
  appWrapper: {
    transform: "translateZ(0)",
  },
  barHeader: {
    backgroundColor: "#0d1b2a",
    display: "flow-root",
    // Clear the iOS status bar in the native shell (needs viewport-fit=cover);
    // resolves to 0 in a normal browser tab.
    paddingTop: "env(safe-area-inset-top)",
  },
  badgeDot: {
    position: "absolute",
    top: 6,
    right: 10,
    minWidth: 14,
    height: 14,
    padding: "0 3px",
    borderRadius: "50%",
    backgroundColor: "#c9a84c",
    border: "1px solid #0d1b2a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
    color: "#0d1b2a",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  badgeDotActive: {
    backgroundColor: "#0d1b2a",
    border: "1px solid #c9a84c",
    color: "#c9a84c",
  },
};

export default function AppModeSwitcher() {
  const [mode, setMode] = useState("coach");
  const [hoveredTab, setHoveredTab] = useState(null);
  const [unreadMatches, setUnreadMatches] = useState(0);
  const mountedRef = useRef(true);

  const pollUnreadCount = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (!data?.session) {
      if (mountedRef.current) setUnreadMatches(0);
      return;
    }
    try {
      const result = await apiFetch("/buddy-finder/unread-count");
      if (mountedRef.current) setUnreadMatches(result.unread_matches || 0);
    } catch (err) {
      // ignore transient polling errors
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // Single timer handle; polling only runs while the tab is visible.
    let interval = null;
    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };
    const startPolling = () => {
      stopPolling();
      pollUnreadCount();
      interval = setInterval(pollUnreadCount, UNREAD_POLL_MS);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) stopPolling();
      else startPolling();
    };

    if (!document.hidden) startPolling();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [pollUnreadCount]);

  return (
    <div>
      <div style={styles.barHeader}>
        <div style={styles.bar}>
          {MODES.map((m) => {
          const isActive = mode === m.id;
          const isHovered = hoveredTab === m.id;
          const showUnreadDot = m.id === "buddyFinder" && unreadMatches > 0;
          return (
            <button
              key={m.id}
              style={{
                ...styles.tab,
                ...(isActive ? styles.tabActive : {}),
                ...(!isActive && isHovered
                  ? { backgroundColor: "rgba(201,168,76,0.12)", color: "#c9a84c" }
                  : {}),
                ...(showUnreadDot ? { position: "relative" } : {}),
              }}
              onClick={() => setMode(m.id)}
              onMouseEnter={() => setHoveredTab(m.id)}
              onMouseLeave={() => setHoveredTab(null)}
            >
              {m.label}
              {showUnreadDot && (
                <span style={{ ...styles.badgeDot, ...(isActive ? styles.badgeDotActive : {}) }}>
                  {unreadMatches > 99 ? "99+" : unreadMatches}
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>

      {/* Always mounted, only hidden: unmounting App mid-analysis left its polling
          running and the remount resumed the same job, saving the session twice. */}
      <div style={mode === "coach" ? styles.appWrapper : { display: "none" }}>
        <App />
      </div>
      {mode === "buddyFinder" && <BuddyFinderMode onMessagesRead={pollUnreadCount} />}
      {mode === "socialFeed" && (
        <SocialFeedMode onEditProfile={() => setMode("buddyFinder")} />
      )}
    </div>
  );
}
